import { JSX, ReactNode } from 'react';

export type TIntroSlide = { url: string; text?: string };

export enum Languages {
  English = 'en',
  Hebrew = 'he',
}

export type TOption = { label: string; value: string; icon?: ReactNode };

export enum ApartmentColumns {
  Id = 'id',
  NrId = 'nrId',
  Building = 'building',
  Number = 'number',
  Type = 'type',
  Area = 'area',
  Status = 'status',
  Rooms = 'rooms',
  Floor = 'floor',
  BalconyArea = 'balconyArea',
  Liked = 'liked',
  HasVideo = 'hasVideo',
  GalleryType = 'galleryType',
  GalleryCount = 'galleryCount',
  GalleryUrls = 'galleryUrls',
  VideoUrl = 'videoUrl',
  MainImage = 'mainImage',
  TourUrl = 'tourUrl',
  FloorPlanUrl = 'floorPlanUrl',
  FloorPlanImage = 'floorPlanImage',
  SpecialInfo = 'specialInfo',
  Price = 'price',
  Elevator = 'elevator',
  Shelter = 'shelter',
  Kind = 'kind',
  balconyViewUrl = 'balconyViewUrl',
}

export type Cell = string | number | React.ReactNode;

export enum ApartmentStatus {
  Sold = 'Sold',
  Available = 'Available',
}

export type ApartmentEntry = {
  [ApartmentColumns.Id]: string;
  [ApartmentColumns.NrId]: number;
  [ApartmentColumns.Building]: string;
  [ApartmentColumns.Number]: number;
  [ApartmentColumns.Type]: string;
  [ApartmentColumns.Area]: number;
  [ApartmentColumns.Status]: ApartmentStatus;
  [ApartmentColumns.Rooms]: number;
  [ApartmentColumns.Floor]: number;
  [ApartmentColumns.BalconyArea]: number;
  [ApartmentColumns.Liked]?: boolean;
  [ApartmentColumns.HasVideo]?: boolean;
  [ApartmentColumns.GalleryType]?: string;
  [ApartmentColumns.GalleryCount]?: number;
  [ApartmentColumns.GalleryUrls]?: string[];
  [ApartmentColumns.VideoUrl]?: string;
  [ApartmentColumns.MainImage]?: string;
  [ApartmentColumns.TourUrl]?: string;
  [ApartmentColumns.FloorPlanUrl]?: string;
  [ApartmentColumns.FloorPlanImage]?: string;
  [ApartmentColumns.SpecialInfo]?: string;
  [ApartmentColumns.Price]?: number;
  [ApartmentColumns.Elevator]?: boolean;
  [ApartmentColumns.Shelter]?: boolean;
  [ApartmentColumns.Kind]?: string;
  [ApartmentColumns.balconyViewUrl]?: string;
};

export enum MobileTabsEnum {
  Inventory = 'inventory',
  Panorama = 'panorama',
  Showcase = 'showcase',
  Landing = 'landing',
  Discount = 'discount',
  ApartmentDetails = 'apartmentDetails',
  ThreeDTour = 'threeDTour',
  FloorPlan = 'floorPlan',
  Balcony = 'balcony',
  LeadForm = 'leadForm',
}

export type MobileTab = { key: MobileTabsEnum; icon: JSX.Element; label: string };

export enum EMainTabs {
  Inventory = 'inventory',
  ThreeDView = 'threeDView',
  Landing = 'landing',
}

export enum ProjectPages {
  Landing = 'landing',
  Showcase = 'showcase',
  Gallery = 'gallery',
  Map = 'map',
  Apartment = 'apartment',
  Panorama = 'panorama',
}
export enum AssetType {
  Gallery = 'gallery',
  Promotion = 'promotion',
  Blueprint = 'blueprint',
  FloorPlan = 'floorPlan',
  Logo = 'logo',
  MainImage = 'mainImage',
  HoverImage = 'hoverImage',
  VsObject = 'vsObject',
  Video = 'video',
  Document = 'document',
  ConstructorLogo = 'constructorLogo',
}

export interface Asset {
  id: number;
  idDraft?: number;
  projectId: number;
  polygonId?: number | null; // Keep for backward compatibility
  listingId?: number | null;
  title: string;
  type: AssetType;
  filePath: string;
  url: string;
  previewUrl?: string;
  urlLarge?: string;
  urlMedium?: string;
  urlSmall?: string;
  order: number;
  yad2Id: number;
  isMain?: boolean;
}

export interface MediaGroup {
  id: number;
  name: string;
  description?: string;
  assets: Asset[];
  usageCount?: number;
  projectId: number;
  createdAt: string;
  updatedAt: string;
}

interface Timeline {
  id: number;
  projectId: number;
  title: string;
  description: string;
  date: string;
}

export interface ProjectPromotion {
  id?: number;
  idDraft?: number;
  projectId: number;
  name: string;
  description: string;
  bgColor: string;
  bgType: string;
  bgIcon: string;
  bgImage: string;
  bgImageId: number;
  bgImageIdDraft?: number;
  bgIconId: number;
  bgIconDraftId?: number;
}

interface Object {
  id: number;
  name: string;
  floors: number;
  type: string;
  projectId: number;
  scaleX: number;
  scaleY: number;
  scaleZ: number;
  positionX: number;
  positionY: number;
  positionZ: number;
  rotationX: number;
  rotationY: number;
  rotationZ: number;
  actionType: string;
  asset: Asset[];
  createdAt: string;
  updatedAt: string;
}
interface Client {
  description: string;
  id: number;
  logo: string;
  logoUrl?: string;
  name: string;
}
export type ProjectSpec = {
  [key: number | string]: string[]; // as in API
};

export enum OrderType {
  ThreeD = '3d',
  TwoD = '2d',
  Combined = 'combined',
}

export enum DecorationType {
  Tiles = 'tiles',
  Splats = 'splats',
  Model = 'model',
}

export enum BackgroundType {
  Equirect = 'equirect',
  Cube = 'cube',
  Multires = 'multires',
}

export type TProject = {
  boosted: boolean;
  isYzer: boolean;
  yad2CrmId: string | null;
  yad2Id?: number | null;
  yzerId: number;
  id: number;
  previewId: string;
  slug: string;
  title: string;
  subTitle: string;
  logoUrl?: string;
  mainImageUrl?: string;
  hoverImageUrl?: string;
  description: string;
  address: string;
  lng: number;
  lat: number;
  type: string[];
  dealType: string;
  status: string;
  price: number[];
  area: number[];
  rooms: number[];
  email: string;
  phoneMobile: string;
  phoneDesktop: string;
  contactName: string;
  specifications: (string | ProjectSpec)[];
  apartmentSpecifications: string[];
  completionDate: string;
  startDate?: string;
  orderType: OrderType;
  yad2StreetId: string;
  yad2HoodId: string;
  yad2CityId: string;
  yad2AreaId: string;
  yad2TopAreaId: string;
  assets: Asset[];
  timelines: Timeline[];
  projectPromotions: ProjectPromotion[];
  objects: Object[];
  listings: TListing[]; // Primary field for apartment/unit data
  clients: Client[];
  withConsent?: boolean;
  products: number[];
  promotions: boolean;
  promotionalLabel?: string;
  immediateOccupancy?: boolean;
  minEntranceDate?: string | Date;
  logosOnMap?: boolean;
  buildingModelPath: string;
  polygonModelPath: string;
  envMapPath: string;
  backgroundType: BackgroundType;
  backgroundPath: string;
  decorationType: DecorationType;
  decorationPath: string;
  propsConfigPath: string;
  createdAt: string;
  updatedAt: string;
  whatsappNumber?: string;
  panoramaXmlPath?: string | null;
};

export type TProjectsApiResponse = {
  status: number;
  message: string;
  timestamp: number;
  data: { projects?: TProject[] };
};

export type TProjectApiResponse = {
  status: number;
  message: string;
  timestamp: number;
  data: { project?: TProject };
};

export enum PolygonType {
  Apartment = 'apartment',
  Patio = 'patio',
  StudioLoft = 'studioLoft',
  Cottage = 'cottage',
  Penthouse = 'penthouse',
  Duplex = 'duplex',
  ApartmentUnit = 'apartmentUnit',
  Offices = 'offices',
  Stores = 'stores',
  Clinics = 'clinics',
  CommercialWarehouses = 'commercialWarehouses',
  CommercialPlots = 'commercialPlots',
  IndustrialStructures = 'industrialStructures',
  Venues = 'venues',
  AgriculturalPlots = 'agriculturalPlots',
  CommercialGeneral = 'commercialGeneral',
  VacationApartments = 'vacationApartments',
  ParkingPlaces = 'parkingPlaces',
  ApartmentSwapping = 'apartmentSwapping',
  Plots = 'plots',
  OfficeBuildings = 'officeBuildings',
  BusinessesForSale = 'businessesForSale',
  ParkingPlots = 'parkingPlots',
  TwoFamilyDwelling = 'twofamilyDwelling',
  General = 'general',
  Sublet = 'sublet',
  ResidentialBuilding = 'residentialBuilding',
  Warehouses = 'warehouses',
  Estate = 'estate',
  Trailers = 'trailers',
  BasementParterres = 'basementParterres',
  Triplex = 'triplex',
  AuxiliaryPlots = 'auxiliaryPlots',
  Basement = 'basement',
  Studio = 'studio',
  Hotel = 'hotel',
  AssistedLiving = 'assistedLiving',
  JointWorkspace = 'jointWorkspace',
  PurchaseGroup = 'purchaseGroup',
}
export enum EPolygonStatus {
  Available = 'available',
  Sold = 'sold',
  Reserved = 'reserved',
  Unavailable = 'unavailable',
}

export enum EProjectPolygonPriceBy {
  Total = 'total',
  Sqm = 'm2',
}

export type TPolygon = {
  id: number;
  projectId: number;
  project: TProject;
  name: string | null;
  scaleX?: number;
  scaleY?: number;
  scaleZ?: number;
  positionX?: number;
  positionY?: number;
  positionZ?: number;
  rotationX?: number;
  rotationY?: number;
  rotationZ?: number;
  projectObjectId: number;
  promotionLabel: string | null;
  rooms: number;
  number: number | null;
  livingArea: number | null;
  balconyArea: number;
  price: number;
  priceBy: EProjectPolygonPriceBy;
  showPrice: boolean;
  minFloor: number;
  maxFloor: number;
  status: EPolygonStatus;
  type: PolygonType;
  assets: Asset[];
  createdAt?: string;
  updatedAt?: string;
  yad2Id?: number | null;
  buildingNumber?: string;
  elevator?: boolean;
  shelter?: boolean;
  order?: number;
  tourUrl?: string;
  floorPlanUrl?: string;
  hideDrawer?: boolean;
  galleryUrls?: string[];
  mainImageUrl?: string;
  mainImageUrlSmall?: string;
  mediaGroup?: MediaGroup;
  mediaGroupId?: number;
  // New database columns for migration
  building?: string;
  showcasePolygonId?: string;
  kind?: string;
  // Hebrew specifications stored as JSON string
  specs?: string;
  useInLockedDrawer?: boolean;
  // Balcony camera metadata for BalconyView
  balconyMetadata?: any;
};

export type TListing = TPolygon;

export type TPolygonApiResponse = {
  status: number;
  message: string;
  timestamp: number;
  data: TPolygon;
};

export type Props = { aptArray?: TPolygon[]; projects?: TProject[]; project?: TProject };

export enum ELandingTabsEnum {
  About = 'About',
  Interactive = 'Interactive', // interactive section had own tabs switching scenarios !
  InteractiveView = 'InteractiveView',
  Promotions = 'Promotions',
  Tours3D = 'Tours3D',
  Specifications = 'Specifications',
  Gallery = 'Gallery',
  Map = 'Map',
  Inventory = 'Inventory',
  Timeline = 'Timeline',
  AboutClient = 'AboutClient',
}

export type TLead = {
  site?: string;
  url?: string;
  platform?: string;
  name: string;
  phone: string;
  email: string;
  lm_form?: number;
  lm_key?: string;
  supplier?: number;
};

export type TPromotion = {
  title?: string;
  description?: string;
  bgColor?: string;
  bgImage?: string;
  textColor?: string;
  overlayColor?: string;
  overlaySecondColor?: string;
  overlayAngle?: number | string;
  cta?: string;
};

export enum ETimelineStepStatus {
  Completed = 'completed',
  Upcoming = 'upcoming',
}

export enum EGTMEvent {
  Lead = 'lead',
  ShowPhoneNumber = 'show_phone_number',
  CallSeller = 'call_seller',
  WhatsappClick = 'whatsapp',
  PromotionClick = 'promotion_click',
  Navigation = 'navigation',
  Share = 'share',
  OpenGallery = 'open_gallery',
  PublicLayer = 'public_layer',
  ApartmentTour = 'apartment_tour',
  Like = 'like',
  ExperienceStart = 'experience_start_click',
  SceneClick = 'tag_360_click',
  POIClick = 'tag_surrounding_click',
  BuildingClick = 'building_click',
  InventoryClick = 'inventory_click',
  FullscreenClick = 'full_screen_click',
  ViewChoiceClick = 'view_choice_click',
  ApartmentTourChoiceClick = 'apartment_tour_choice_click',
  ContactIntent = 'contact_intent',
  LeadFormPresented = 'lead_form_presented',
  LeadFormClosed = 'lead_form_closed',
  MapViewClick = 'map_view_click',
  LeadClick = 'lead_click',
  LeadFormFilledFirst = 'lead_form_filled_first',
  LeadFormFilledSecond = 'lead_form_filled_second',
  LeadFormFilledThird = 'lead_form_filled_third',
}

export enum EShowcaseType {
  Default = 'Default',
  Panorama = 'Panorama',
}

export type TProjectObject = {
  id: number;
  projectId: number;
  name: string;
  assets: { id: number; filePath: string }[];
  projectObjectFields: { fieldName: string; value: string }[];
  positionX: number;
  positionY: number;
  positionZ: number;
  rotationX: number;
  rotationY: number;
  rotationZ: number;
  scaleX: number;
  scaleY: number;
  scaleZ: number;
};

export type TProjectObjectField = {
  createdAt: string;
  defaultValue: string;
  enumValues: null;
  fieldLabel: string;
  fieldName: string;
  fieldType: string;
  id: number;
  isRequired: boolean;
  showcaseEntityType: string;
  updatedAt: string;
  value: string;
};

export type TTagProjectObject = TProjectObject & {
  assets: any[];
  projectObjectFields: TProjectObjectField[];
};
